import { AccountMergeRequest } from './AccountMergeRequest'
import {
  mapAccountMergeRequestFilterOptionToResource,
  mapAccountMergeRequestResourceToProps,
} from './account-merge-request-mappers'
import { type CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { collection } from '@/infra/collection'
import { Id } from '@/infra/id'
import { type AdminIamApi } from '@/features/admin/api/admin-iam-api'

/**
 * Whether to list the merge requests that await action, or the ones that
 * were acted on.
 */
export type AccountMergeRequestFilterOption = 'Pending' | 'Processed'

export interface ListMergeRequestsQuery extends CursorPageQuery {
  filter?: AccountMergeRequestFilterOption
}

export class AccountMergeRequestStore {
  /**
   * The account merge requests.
   */
  readonly mergeRequests = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new AccountMergeRequest(),
  })

  constructor(private readonly _api: AdminIamApi) {}

  /**
   * Lists the account merge requests matching the filter.
   */
  async listMergeRequests(
    { filter = 'Pending', ...paging }: ListMergeRequestsQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<AccountMergeRequest>> {
    const response = await this._api.accountMergeRequests.list(
      {
        filter: mapAccountMergeRequestFilterOptionToResource(filter),
        ...paging,
      },
      signal,
    )
    return {
      next: response.cursor,
      data: this.mergeRequests.set(
        response.data.map(mapAccountMergeRequestResourceToProps),
      ),
    }
  }

  /**
   * Merges the request's duplicate account into its primary account.
   */
  async link(id: Id) {
    const resource = await this._api.accountMergeRequests.link(Id.assert(id))
    return this.mergeRequests.set(
      mapAccountMergeRequestResourceToProps(resource),
    )
  }

  /**
   * Rejects the merge request.
   */
  async reject(id: Id) {
    const resource = await this._api.accountMergeRequests.reject(Id.assert(id))
    return this.mergeRequests.set(
      mapAccountMergeRequestResourceToProps(resource),
    )
  }

  /**
   * Ignores the merge request.
   */
  async ignore(id: Id) {
    const resource = await this._api.accountMergeRequests.ignore(Id.assert(id))
    return this.mergeRequests.set(
      mapAccountMergeRequestResourceToProps(resource),
    )
  }
}
